import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import MainLayout from '../../components/layout/MainLayout';
import Button from '../../components/ui/Button';
import workerProfileService from '../../services/workerProfileService';
import serviceAreaContentService from '../../services/serviceAreaContentService';

const JobSectorBrowserCraneOperators = () => {
  const [activeFilter, setActiveFilter] = useState('alle');
  const [selectedSpecialization, setSelectedSpecialization] = useState('all');
  const [locationRadius, setLocationRadius] = useState('50');
  const [certifiedOperators, setCertifiedOperators] = useState([]);
  const [loadingOperators, setLoadingOperators] = useState(true);
  const [news, setNews] = useState([]);
  const [areaContent, setAreaContent] = useState([]);
  const [loadingContent, setLoadingContent] = useState(true);

  useEffect(() => {
    loadWorkerProfiles();
    loadNewsAndContent();
  }, []);

  const loadWorkerProfiles = async () => {
    try {
      setLoadingOperators(true);
      const data = await workerProfileService?.getWorkerProfilesByServiceArea('Kraan Operators');
      setCertifiedOperators(data || []);
    } catch (error) {
      console.error('Error loading worker profiles:', error);
    } finally {
      setLoadingOperators(false);
    }
  };

  const loadNewsAndContent = async () => {
    try {
      setLoadingContent(true);
      const [newsData, contentData] = await Promise.all([
        serviceAreaContentService?.getNewsByServiceArea('Kraan Operators'),
        serviceAreaContentService?.getContentByServiceArea('Kraan Operators')
      ]);
      setNews(newsData || []);
      setAreaContent(contentData || []);
    } catch (error) {
      console.error('Error loading news and content:', error);
    } finally {
      setLoadingContent(false);
    }
  };

  const craneJobs = [
    {
      title: 'Torenkraan operator hoogbouw project',
      location: 'Amsterdam',
      budget: '€4.500 - €6.500',
      deadline: '16 weken',
      description: 'Torenkraan bediening voor hoogbouw project 25 verdiepingen',
      craneType: 'Torenkraan',
      capacity: '16 ton',
      height: '80 meter',
      shiftType: 'Dagdienst',
      certification: 'TCO certificaat, VCA',
      hourlyRate: '€45',
      experience: '5+ jaar',
      complexity: 'high'
    },
    {
      title: 'Mobiele kraan industriële installatie',
      location: 'Rotterdam',
      budget: '€3.200 - €4.800',
      deadline: '8 weken',
      description: 'Mobiele kraan voor plaatsing zware industriële machines',
      craneType: 'Mobiele kraan',
      capacity: '100 ton',
      height: 'N/A',
      shiftType: 'Flexibel',
      certification: 'MCO certificaat, VCA, Hijsplan',
      hourlyRate: '€55',
      experience: '8+ jaar',
      complexity: 'high'
    },
    {
      title: 'Rupskraan grondverzet project',
      location: 'Utrecht',
      budget: '€2.800 - €4.000',
      deadline: '12 weken',
      description: 'Rupskraan bediening voor grootschalig grondverzet nieuwbouwwijk',
      craneType: 'Rupskraan',
      capacity: '40 ton',
      height: 'N/A',
      shiftType: 'Dagdienst',
      certification: 'Machinist certificaat, VCA',
      hourlyRate: '€42',
      experience: '4+ jaar',
      complexity: 'medium'
    },
    {
      title: 'Autokraan onderhoudswerkzaamheden',
      location: 'Den Haag',
      budget: '€1.800 - €2.600',
      deadline: '4 weken',
      description: 'Autokraan voor diverse onderhoudswerkzaamheden bedrijventerrein',
      craneType: 'Autokraan',
      capacity: '35 ton',
      height: 'N/A',
      shiftType: 'Dagdienst',
      certification: 'MCO certificaat, VCA',
      hourlyRate: '€38',
      experience: '3+ jaar',
      complexity: 'low'
    },
    {
      title: 'Telescoopkraan brug constructie',
      location: 'Eindhoven',
      budget: '€5.500 - €7.500',
      deadline: '20 weken',
      description: 'Telescoopkraan voor plaatsing brugdelen infrastructuurproject',
      craneType: 'Telescoopkraan',
      capacity: '200 ton',
      height: 'N/A',
      shiftType: 'Nachtdienst mogelijk',
      certification: 'MCO certificaat, VCA, Hijsplan specialist',
      hourlyRate: '€65',
      experience: '10+ jaar',
      complexity: 'high'
    },
    {
      title: 'Minikraan binnenstedelijke renovatie',
      location: 'Groningen',
      budget: '€1.400 - €2.200',
      deadline: '6 weken',
      description: 'Minikraan voor renovatiewerkzaamheden in smalle binnenstad',
      craneType: 'Minikraan',
      capacity: '5 ton',
      height: 'N/A',
      shiftType: 'Dagdienst',
      certification: 'Machinist certificaat, VCA',
      hourlyRate: '€35',
      experience: '2+ jaar',
      complexity: 'low'
    },
    {
      title: 'Havenkraan containerterminal',
      location: 'Rotterdam Haven',
      budget: '€6.000 - €8.500',
      deadline: '24 weken',
      description: 'Havenkraan bediening voor containerterminal operaties',
      craneType: 'Havenkraan',
      capacity: '65 ton',
      height: '45 meter',
      shiftType: '3-ploegendienst',
      certification: 'Havenkraan certificaat, VCA, Veiligheid haven',
      hourlyRate: '€48',
      experience: '6+ jaar',
      complexity: 'high'
    },
    {
      title: 'Torenkraan windpark installatie',
      location: 'Flevoland',
      budget: '€5.000 - €7.000',
      deadline: '14 weken',
      description: 'Torenkraan voor plaatsing windturbine componenten',
      craneType: 'Torenkraan',
      capacity: '120 ton',
      height: '100 meter',
      shiftType: 'Dagdienst',
      certification: 'TCO certificaat, VCA, Windenergie specialist',
      hourlyRate: '€58',
      experience: '7+ jaar',
      complexity: 'high'
    }
  ];

  const specializations = [
    { value: 'all', label: 'Alle kraantypen' },
    { value: 'tower-crane', label: 'Torenkraan' },
    { value: 'mobile-crane', label: 'Mobiele kraan' },
    { value: 'crawler-crane', label: 'Rupskraan' },
    { value: 'truck-crane', label: 'Autokraan' },
    { value: 'telescopic-crane', label: 'Telescoopkraan' },
    { value: 'mini-crane', label: 'Minikraan' },
    { value: 'harbor-crane', label: 'Havenkraan' }
  ];

  const getComplexityColor = (complexity) => {
    const colors = {
      high: 'bg-error/10 text-error border-error/20',
      medium: 'bg-warning/10 text-warning border-warning/20',
      low: 'bg-success/10 text-success border-success/20'
    };
    return colors?.[complexity] || colors?.low;
  };

  const getComplexityLabel = (complexity) => {
    const labels = {
      high: 'Complex',
      medium: 'Gemiddeld',
      low: 'Basis'
    };
    return labels?.[complexity] || 'Basis';
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <motion.section 
          className="bg-gradient-to-br from-slate-50 to-gray-50 py-16 md:py-24"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
                <Icon name="ArrowLeft" size={16} />
                Terug naar home
              </Link>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center">
                  <Icon name="Truck" size={32} className="text-slate-600" />
                </div>
                <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground">
                  Kraanoperators - Vakgebied Browser
                </h1>
              </div>
              <p className="text-lg text-muted-foreground max-w-3xl">
                Gespecialiseerde kraanoperators voor hoogbouw, infrastructuur, industrie en zware hijswerkzaamheden met alle kraantypen.
              </p>
            </motion.div>
          </div>
        </motion.section>

        {/* Stats Section */}
        <section className="py-12 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { label: 'Actieve opdrachten', value: '89', icon: 'Briefcase' },
                { label: 'Gecertificeerde operators', value: '156', icon: 'Users' },
                { label: 'Gemiddelde rating', value: '4.8', icon: 'Star' },
                { label: 'Voltooide projecten', value: '4.1k+', icon: 'CheckCircle' }
              ]?.map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Icon name={stat?.icon} size={24} className="text-primary mx-auto mb-2" />
                  <div className="text-3xl font-bold text-foreground mb-1">{stat?.value}</div>
                  <div className="text-sm text-muted-foreground">{stat?.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section className="py-8 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="bg-white border-2 border-slate-200 rounded-xl p-6" style={{
              boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
            }}>
              <h3 className="font-heading font-semibold text-lg text-foreground mb-4">Geavanceerde Filters</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Kraantype</label>
                  <select 
                    value={selectedSpecialization}
                    onChange={(e) => setSelectedSpecialization(e?.target?.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {specializations?.map(spec => (
                      <option key={spec?.value} value={spec?.value}>{spec?.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Locatie Radius</label>
                  <select 
                    value={locationRadius}
                    onChange={(e) => setLocationRadius(e?.target?.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="10">Binnen 10 km</option>
                    <option value="25">Binnen 25 km</option>
                    <option value="50">Binnen 50 km</option>
                    <option value="100">Binnen 100 km</option>
                    <option value="nationwide">Landelijk</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Certificering</label>
                  <select className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="all">Alle certificeringen</option>
                    <option value="tco">TCO certificaat</option>
                    <option value="mco">MCO certificaat</option>
                    <option value="vca">VCA</option>
                    <option value="hijsplan">Hijsplan specialist</option>
                    <option value="machinist">Machinist certificaat</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Jobs Section */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-heading font-bold text-foreground">Beschikbare Kraanopdrachten</h2>
              <div className="flex gap-2">
                <Button
                  variant={activeFilter === 'alle' ? 'primary' : 'outline'}
                  onClick={() => setActiveFilter('alle')}
                >
                  Alle ({craneJobs?.length})
                </Button>
                <Button
                  variant={activeFilter === 'urgent' ? 'primary' : 'outline'}
                  onClick={() => setActiveFilter('urgent')}
                >
                  Urgent
                </Button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {craneJobs?.map((job, index) => (
                <motion.div
                  key={index}
                  className="bg-white border-2 border-slate-200 rounded-xl p-6 hover:border-primary transition-all duration-300"
                  style={{
                    boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-heading font-semibold text-lg text-foreground">{job?.title}</h3>
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium border ${getComplexityColor(job?.complexity)}`}>
                      {getComplexityLabel(job?.complexity)}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="MapPin" size={16} />
                      <span>{job?.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Calendar" size={16} />
                      <span>Deadline: {job?.deadline}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Clock" size={16} />
                      <span>{job?.shiftType}</span>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground mb-4">{job?.description}</p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Kraantype:</span>
                      <span className="font-medium text-foreground">{job?.craneType}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Capaciteit:</span>
                      <span className="font-medium text-foreground">{job?.capacity}</span>
                    </div>
                    {job?.height !== 'N/A' && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Hoogte:</span>
                        <span className="font-medium text-foreground">{job?.height}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Uurloon:</span>
                      <span className="font-semibold text-primary">{job?.hourlyRate}</span>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-3 mb-4">
                    <div className="text-xs text-muted-foreground mb-1">Vereiste certificering</div>
                    <div className="text-sm font-medium text-foreground">{job?.certification}</div>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-3 mb-4">
                    <div className="text-xs text-muted-foreground mb-1">Minimale ervaring</div>
                    <div className="text-sm font-medium text-foreground">{job?.experience}</div>
                  </div>

                  <div className="flex gap-2">
                    <Button variant="primary" className="flex-1">
                      Solliciteren
                    </Button>
                    <Button variant="outline">
                      <Icon name="Info" size={16} />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Certified Operators Section */}
        <section className="py-12 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-heading font-bold text-foreground">Gecertificeerde Kraan Operators</h2>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Users" size={16} />
                <span>{certifiedOperators?.length} operators beschikbaar</span>
              </div>
            </div>

            {loadingOperators ? (
              <div className="text-center py-8 text-muted-foreground">Laden...</div>
            ) : certifiedOperators?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">Geen operators beschikbaar</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {certifiedOperators?.map((operator, index) => (
                  <motion.div
                    key={operator?.id || index}
                    className="bg-white border-2 border-slate-200 rounded-xl p-6"
                    style={{
                      boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{operator?.name}</h3>
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex items-center gap-1">
                            <Icon name="Star" size={14} className="text-warning fill-warning" />
                            <span className="text-sm font-medium text-foreground">{operator?.rating}</span>
                            <span className="text-xs text-muted-foreground">({operator?.reviews} reviews)</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Icon name="MapPin" size={14} />
                          <span>{operator?.location}</span>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="text-sm font-medium text-foreground mb-2">Specialisatie</div>
                      <div className="text-sm text-primary font-medium">{operator?.specialization}</div>
                    </div>

                    <div className="mb-4">
                      <div className="text-sm font-medium text-foreground mb-2">Certificeringen</div>
                      <div className="flex flex-wrap gap-2">
                        {operator?.certifications?.map((cert, idx) => (
                          <span key={idx} className="px-2 py-1 bg-success/10 text-success text-xs rounded-full border border-success/20">
                            {cert}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="text-sm font-medium text-foreground mb-2">Verzekering</div>
                      <div className="text-sm text-muted-foreground">{operator?.warranty}</div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Ervaring</div>
                        <div className="text-sm font-medium text-foreground">{operator?.experience}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Voltooide projecten</div>
                        <div className="text-sm font-medium text-foreground">{operator?.completed_projects}+</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Uurtarief</div>
                        <div className="text-xl font-bold text-foreground">{operator?.hourly_rate}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground mb-1">Beschikbaarheid</div>
                          <div className="text-sm font-medium text-success">{operator?.availability}</div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="ml-2"
                        >
                          <Icon name="Phone" size={14} />
                          Contacteren
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Area Content Section */}
        {!loadingContent && areaContent?.length > 0 && (
          <section className="py-12">
            <div className="max-w-7xl mx-auto px-4 lg:px-6">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-8">Waarom kiezen voor onze kraan operators?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {areaContent?.map((content, index) => (
                  <motion.div
                    key={content?.id}
                    className="bg-white border-2 border-slate-200 rounded-xl p-6"
                    style={{
                      boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="CheckCircle" size={24} className="text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{content?.title}</h3>
                        <p className="text-sm text-muted-foreground">{content?.content}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Latest News Section */}
        {!loadingContent && news?.length > 0 && (
          <section className="py-12 bg-muted/30">
            <div className="max-w-7xl mx-auto px-4 lg:px-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-heading font-bold text-foreground">Laatste Nieuws</h2>
                <Icon name="Newspaper" size={24} className="text-primary" />
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {news?.map((newsItem, index) => (
                  <motion.div
                    key={newsItem?.id}
                    className="bg-white border-2 border-slate-200 rounded-xl p-6"
                    style={{
                      boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                      <Icon name="Calendar" size={14} />
                      <span>{new Date(newsItem?.published_date)?.toLocaleDateString('nl-NL')}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-foreground mb-3">{newsItem?.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{newsItem?.content}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="User" size={14} />
                      <span>{newsItem?.author}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-primary/5 to-primary/10">
          <div className="max-w-4xl mx-auto px-4 lg:px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
                Klaar om te starten met uw kraanproject?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Vind de juiste gecertificeerde kraanoperator voor uw hoogbouw, infrastructuur of industrieel project.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="primary" size="lg">
                  <Icon name="Plus" size={20} className="mr-2" />
                  Opdracht Plaatsen
                </Button>
                <Button variant="outline" size="lg">
                  <Icon name="Users" size={20} className="mr-2" />
                  Operator Worden
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </MainLayout>
  );
};

export default JobSectorBrowserCraneOperators;